import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import { useNotification } from './useNotification';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { CaseQuestionI, CaseQuestionsPreEvaluate } from '@/interfaces';
import { casesService, GET_CASE_ID } from '@/api';
import {
	customEvent,
	getUUID,
	getUserIp,
	pushLeadJourneyEvent,
	setLeadQualificationOutcome,
} from '@/lib';
import { useQuery } from '@tanstack/react-query';
import AppRoutes from '@/const/routes';
import { format, parse } from 'date-fns';
import { AvatarItem } from '@/components/common/avatar/avatarGroup';
import Icons from '@/const/icons';

interface ListQuestionsI extends CaseQuestionI {
	answer?: string;
	options?: any[];
}

interface Props {
	id: string;
	uuid: string;
	questions: CaseQuestionI[];
}

export const useSurvey = ({ id, uuid, questions }: Props) => {
	const router = useRouter();
	const session = useSession();
	const { toastError } = useNotification();
	const [loading, setLoading] = useState(false);
	const [listQuestions, setListQuestions] =
		useState<ListQuestionsI[]>(questions);
	const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);
	const hasTrackedFormStartRef = useRef(false);

	const trackFormStarted = (): void => {
		if (hasTrackedFormStartRef.current) return;

		hasTrackedFormStartRef.current = true;
		pushLeadJourneyEvent('form_started', {
			dedupKey: `form_started:${id}`,
			extra: {
				case_id: uuid,
				case_slug: id,
			},
		});
	};

	const { register, setValue, watch } = useForm({
		mode: 'onChange',
	});

	useEffect(() => {
		setListQuestions(questions);
	}, [questions]);

	useEffect(() => {
		if (listQuestions) {
			const isButtonDisabled = listQuestions.some((question) => {
				return question.question_type !== 'MULTIPLE'
					? !question.answer
					: question.multiple_choise_questions.every(
							(option) => !option.answer
					  );
			});

			setIsButtonDisabled(isButtonDisabled);
		}
	}, [listQuestions]);

	const { data: currentCaseRes } = useQuery({
		queryKey: [GET_CASE_ID, id],
		queryFn: () => casesService.getById(id as string),
		enabled: !!id,
		refetchOnWindowFocus: false,
	});

	const onChange = (value: string, id: number): void => {
		trackFormStarted();

		const questionsResponse = listQuestions.map((question) => {
			if (question.id === id) {
				question = {
					...question,
					answer: value,
				};
			}
			return question;
		});

		setListQuestions(questionsResponse);
	};

	const onMultipleChange = (questionId: number, optionId: number) => {
		trackFormStarted();

		const questionsResponse = listQuestions.map((question) => {
			if (question.id === questionId) {
				question.multiple_choise_questions =
					question.multiple_choise_questions.map((option) => {
						if (option.id === optionId) {
							return {
								...option,
								answer: !option.answer,
							};
						}
						return option;
					});
			}

			return question;
		});
		setListQuestions(questionsResponse);
	};

	const getAnswers = (): any[] => {
		return listQuestions.map((question) => ({
			questionId: question.id,
			answer:
				question.question_type === 'DATE'
					? format(
							parse(question.answer || '', 'MM-dd-yyyy', new Date()),
							'MM/dd/yyyy'
					  )
					: question.answer || '',
			multiple_responses:
				question.question_type === 'MULTIPLE'
					? question.multiple_choise_questions.map((option) => ({
							multipleQuestionId: option.id,
							answer: !!option.answer,
					  }))
					: [],
		}));
	};

	const handleSubmitWithSession = async (): Promise<any> => {
		try {
			const answers = getAnswers();
			const userId = session.data?.user.user.id;
			const body = {
				user_id: userId,
				responses: answers,
			};
			return casesService.evaluateClaim(id, body);
		} catch (error: any) {
			if (error.message === "You can't apply it to the same case") {
				toastError(
					'You’ve already applied to this case. You can view your submitted application in your My Cases page.'
				);
			} else {
				toastError('An error has occurred. Please try again later.');
			}
		}
	};

	const handleSubmitWithoutSession = async (): Promise<any> => {
		try {
			const answers = getAnswers();
			const uuid = getUUID();
			const userIp = await getUserIp();
			const ip = `${uuid}${userIp}`;
			const body: CaseQuestionsPreEvaluate = {
				ip_user: ip,
				responses: answers,
			};
			const response = await casesService.preEvaluateClaim(id, body);
			await fetch('/api/cookie/pre-application', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ user_id_application: ip }),
			});
			sessionStorage.setItem('user_id_application', ip);
			return response;
		} catch (error: any) {
			if (error.message === "You can't apply it to the same case") {
				toastError(
					'You’ve already applied to this case. You can view your submitted application in your My Cases page.'
				);
			} else {
				toastError('An error has occurred. Please try again later.');
			}
		}
	};

	const onClickSubmit = async (): Promise<void> => {
		try {
			let response: any = '';
			setLoading(true);

			if (session.data) {
				response = await handleSubmitWithSession();
			} else {
				response = await handleSubmitWithoutSession();
			}

			if (!response || typeof response.data !== 'boolean') {
				return;
			}

			pushLeadJourneyEvent('form_submitted', {
				dedupKey: `form_submitted:${id}`,
				extra: {
					case_id: uuid,
					case_slug: id,
				},
			});

			const qualified = response.data === true;
			setLeadQualificationOutcome(qualified, uuid);

			pushLeadJourneyEvent(
				qualified ? 'lead_qualified' : 'lead_did_not_qualify',
				{
					dedupKey: `${
						qualified ? 'lead_qualified' : 'lead_did_not_qualify'
					}:${id}`,
					extra: {
						case_id: uuid,
						case_slug: id,
						qualification_outcome: qualified ? 'qualified' : 'not_qualified',
					},
				}
			);

			const redirect = session.data
				? `${AppRoutes.CLIENTAPP_DASHBOARD}?evaluated=${
						response.data === true
				  }&case_id=${uuid}`
				: `${AppRoutes.LANDING_CLAIM_EVALUATION}?evaluated=${
						response.data === true
				  }&case_id=${uuid}`;

			customEvent({
				action: 'file_your_claim_click',
				category: 'landing',
				label: 'File your claim',
				value: 'File your claim',
				callback: () => router.push(redirect),
			});
		} catch (error: any) {
			if (error.message === "You can't apply it to the same case") {
				toastError(
					'You’ve already applied to this case. You can view your submitted application in your My Cases page.'
				);
			} else {
				toastError('An error has occurred. Please try again later.');
			}
		} finally {
			setLoading(false);
		}
	};

	const avatarItems: AvatarItem[] = [
		{
			url: Icons.userThree,
		},
		{
			url: Icons.userTwo,
		},
		{
			url: Icons.userOne,
		},
	];

	return {
		loading,
		onChange,
		listQuestions,
		isButtonDisabled,
		onClickSubmit,
		onMultipleChange,
		register,
		setValue,
		currentCaseRes,
		watch,
		avatarItems,
	};
};
